import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ChangelogEntry } from '../_models/changelog';
import {
  AddDevicesToDraftExperimentResponse,
  ChangelogResponse,
  DeviceErrorResponse,
  ExperimentIndexResponse,
} from 'src/app/_models/api-responses';
import { Experiment } from '../_models/experiment';
import {
  ExperimentDeviceUpdate,
  ExperimentUpdate,
} from '../_models/experiment-update';
import { CreateExperiment } from '../_models/experiment-create';
import { PaginationData } from '../_models/pagination-data.model';
import { ExperimentStatus } from '../_models/experiment-status';
import { Module } from '../_models/module-enum';
import { httpOptions } from '../_helpers/utils';
import { AuthenticationService } from './authentication.service';
import { ExperimentMode } from '../_models/experiment-mode';
import { DeviceError } from '../_models/device-error';
import { InformationBannerType } from '../_models/information-banner';
import { ExperimentTemplate } from '../_models/experiment-template';

@Injectable({
  providedIn: 'root',
})
export class ExperimentService implements OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly authenticationService = inject(AuthenticationService);

  httpOptions = httpOptions;
  currentLaboratoryId: number;

  // Pagination and sort/filter observables for dashboard index view
  private readonly tableSort$ = new BehaviorSubject<{
    sortBy: string;
    sortDirection: string;
  }>(null);
  private readonly tableFilter$ = new BehaviorSubject<string[]>([]);
  private readonly pageNumber$ = new BehaviorSubject<number>(null);
  private readonly pageSize$ = new BehaviorSubject<number>(null);
  private readonly changelogPageNumber$ = new BehaviorSubject<number>(null);
  private readonly changelogPageSize$ = new BehaviorSubject<number>(null);
  private readonly deviceErrorPageNumber$ = new BehaviorSubject<number>(null);
  private readonly deviceErrorPageSize$ = new BehaviorSubject<number>(null);
  private readonly unsavedParametersCache$ = new BehaviorSubject<
    CreateExperiment | Experiment | ExperimentUpdate | ExperimentTemplate | null
  >(null);

  private readonly destroy$ = new Subject<void>();

  constructor() {
    this.authenticationService.selectedLab$
      .pipe(takeUntil(this.destroy$))
      .subscribe((x) => {
        this.currentLaboratoryId = x?.laboratoryId;
      });
  }

  onUpdateSort = (sort: { sortBy: string; sortDirection: string }): void => {
    this.tableSort$.next(sort);
  };

  onUpdatePageNumber = (pageNumber: number): void => {
    this.pageNumber$.next(pageNumber);
  };

  onUpdatePageSize = (pageSize: number): void => {
    this.pageSize$.next(pageSize);
  };

  onUpdateChangelogPageNumber = (pageNumber: number): void => {
    this.changelogPageNumber$.next(pageNumber);
  };

  onUpdateChangelogPageSize = (pageSize: number): void => {
    this.changelogPageSize$.next(pageSize);
  };

  onUpdateDeviceErrorPageNumber = (pageNumber: number): void => {
    this.deviceErrorPageNumber$.next(pageNumber);
  };

  onUpdateDeviceErrorPageSize = (pageSize: number): void => {
    this.deviceErrorPageSize$.next(pageSize);
  };

  cacheUnsavedParameters = (
    formValues: CreateExperiment | Experiment | ExperimentUpdate | ExperimentTemplate,
  ): void => {
    this.unsavedParametersCache$.next(formValues);
    sessionStorage.setItem(
      'unsavedParametersCache',
      JSON.stringify(formValues),
    );
  };

  public get sort(): { sortBy: string; sortDirection: string } {
    return this.tableSort$.value;
  }

  private getCachedPagination(
    pageNumber$: BehaviorSubject<number>,
    pageSize$: BehaviorSubject<number>,
  ): { pageNumber: number; pageSize: number } | null {
    const pageNumber = pageNumber$.value;
    const pageSize = pageSize$.value;
    return pageNumber !== null && pageSize !== null
      ? { pageNumber, pageSize }
      : null;
  }

  public get paginationCache(): {
    pageNumber: number;
    pageSize: number;
  } | null {
    return this.getCachedPagination(this.pageNumber$, this.pageSize$);
  }

  public get changelogPaginationCache(): {
    pageNumber: number;
    pageSize: number;
  } | null {
    return this.getCachedPagination(
      this.changelogPageNumber$,
      this.changelogPageSize$,
    );
  }

  public get deviceErrorPaginationCache(): {
    pageNumber: number;
    pageSize: number;
  } | null {
    return this.getCachedPagination(
      this.deviceErrorPageNumber$,
      this.deviceErrorPageSize$,
    );
  }

  public get filter(): string[] {
    return this.tableFilter$.value;
  }

  public get retrieveAndClearUnsavedCache():
    | CreateExperiment
    | Experiment
    | ExperimentUpdate
    | ExperimentTemplate
    | null {
    const cached = this.unsavedParametersCache$.value;
    this.clearUnsavedCache();
    return cached;
  }

  clearUnsavedCache(): void {
    this.unsavedParametersCache$.next(null);
  }

  // HTTP GET /EXPERIMENT
  getExperimentById(id: number): Observable<Experiment> {
    return this.http
      .get<Experiment>(`${environment.apiUrl}/experiments/${id}`)
      .pipe(
        map((response) => {
          return response;
        }),
      );
  }

  // HTTP GET /EXPERIMENT
  getExperiments(
    laboratoryId: number,
    sortBy = 'CreatedAt',
    sortOrder = 'desc',
    pageSize = 5,
    pageNumber = 1,
    experimentStatusId: ExperimentStatus = null,
    experimentModeId: ExperimentMode = null,
    moduleList: Module[] = null,
    search: string = null,
    includeDeleted: boolean = null,
  ): Observable<ExperimentIndexResponse> {
    let url = `${environment.apiUrl}/experiments?LaboratoryId=${laboratoryId}&`;
    if (moduleList?.length > 0) {
      moduleList.forEach((m) => {
        url = url + `Modules=${m}&`;
      });
    }
    url =
      url +
      `OrderBy=${sortBy}&OrderByDirection=${sortOrder}&PageSize=${pageSize}&PageNumber=${pageNumber}`;
    if (experimentStatusId) {
      url = url + `&ExperimentStatus=${experimentStatusId}`;
    }
    if (experimentModeId) {
      url = url + `&ExperimentMode=${experimentModeId}`;
    }
    if (search) {
      url = url + `&search=${search}`;
    }
    if (includeDeleted !== null) {
      url = url + `&includeDeleted=${includeDeleted.toString()}`;
    }
    return this.http
      .get<{
        experiments: Experiment[];
        displayInformationBanner: boolean;
        informationBannerMessage: string;
        informationBannerType: InformationBannerType;
      }>(url, this.httpOptions)
      .pipe(
        map((response) => {
          const experimentList: Experiment[] = [];
          // Set pagination data
          let paginationData: PaginationData | null = null;
          try {
            const rawPagination = response.headers.get('X-Pagination');
            paginationData = rawPagination
              ? (JSON.parse(rawPagination) as PaginationData)
              : null;
          } catch {
            paginationData = null;
          }
          const body = response.body;
          if (response.status === 200 && body) {
            body.experiments.forEach((e: Experiment) => {
              experimentList.push(e);
            });
          }
          return {
            paginationData,
            experimentList,
            displayInformationBanner: body?.displayInformationBanner ?? false,
            informationBannerMessage: body?.informationBannerMessage ?? '',
            informationBannerType: body?.informationBannerType ?? null,
          };
        }),
      );
  }

  // HTTP PATCH /EXPERIMENT
  updateDraftExperiment(
    updatedExperiment: ExperimentUpdate,
  ): Observable<Experiment> {
    const body = {
      ...updatedExperiment,
      laboratoryId: this.currentLaboratoryId,
      fluorescenceChannels: updatedExperiment.fluorescenceChannels?.map((ch) => ch.memorySlot),
    };
    return this.http
      .patch<Experiment>(
        `${environment.apiUrl}/experiments/${updatedExperiment.id}/draft-params`,
        body,
      )
      .pipe(
        map((response: Experiment) => {
          return response;
        }),
      );
  }

  addDevicesToDraftExperiment(
    experimentId: number,
    deviceCalibrations: ExperimentDeviceUpdate,
  ): Observable<AddDevicesToDraftExperimentResponse> {
    return this.http
      .patch<AddDevicesToDraftExperimentResponse>(
        `${environment.apiUrl}/experiments/${experimentId}/draft-devices`,
        deviceCalibrations,
      )
      .pipe(
        map((response: AddDevicesToDraftExperimentResponse) => {
          // TODO: Check if this is necessary
          return response;
        }),
      );
  }

  lockDraftExperiment(experimentId: number): Observable<Experiment> {
    return this.http
      .patch<Experiment>(
        `${environment.apiUrl}/experiments/${experimentId}/lock-draft`,
        '',
      )
      .pipe(
        map((response: Experiment) => {
          return response;
        }),
      );
  }

  // HTTP POST /EXPERIMENT
  startExperiment(experimentId: number): Observable<void> {
    return this.http
      .patch<string>(
        `${environment.apiUrl}/experiments/${experimentId}/start-experiment`,
        '',
      )
      .pipe(
        map(() => {
          return;
        }),
      );
  }

  // HTTP PATCH /EXPERIMENT
  updateActiveExperiment(
    updatedExperiment: ExperimentUpdate,
  ): Observable<Experiment> {
    const body = {
      ...updatedExperiment,
      laboratoryId: this.currentLaboratoryId,
      fluorescenceChannels: updatedExperiment.fluorescenceChannels?.map((ch) => ch.memorySlot),
    };
    return this.http
      .patch<Experiment>(
        `${environment.apiUrl}/experiments/${updatedExperiment.id}/active`,
        body,
      )
      .pipe(
        map((response: Experiment) => {
          return response;
        }),
      );
  }

  // HTTP POST /EXPERIMENT
  createExperiment(newExperiment: CreateExperiment | ExperimentTemplate): Observable<Experiment> {
    const body = {
      ...newExperiment,
      laboratoryId: this.currentLaboratoryId,
      fluorescenceChannels: newExperiment.fluorescenceChannels?.map((ch) => ch.memorySlot),
    };
    return this.http
      .post<Experiment>(`${environment.apiUrl}/experiments`, body)
      .pipe(
        map((response: Experiment) => {
          return response;
        }),
      );
  }

  // HTTP PUT /experiment/experimentId?pauseOrResume=true
  pauseDevicesOnExperiment(
    experimentId: string,
    pauseOrResume: string,
  ): Observable<string> {
    return this.http
      .put<string>(
        `${environment.apiUrl}/experiments/${experimentId}/pause?pauseOrResume=${pauseOrResume}`,
        '',
      )
      .pipe(
        map((response: string) => {
          return response;
        }),
      );
  }

  // HTTP GET /CHANGELOG
  getExperimentChangelog(
    id: number,
    sortBy = 'CreatedAt',
    sortOrder = 'asc',
    pageSize = 10,
    pageNumber = 1,
  ): Observable<ChangelogResponse> {
    return this.http
      .get<
        ChangelogEntry[]
      >(`${environment.apiUrl}/change-logs?OrderBy=${sortBy}&OrderByDirection=${sortOrder}&PageSize=${pageSize}&PageNumber=${pageNumber}&ExperimentId=${id}`, this.httpOptions)
      .pipe(
        map((response) => {
          // Set pagination data
          const paginationData = JSON.parse(
            response.headers.get('X-Pagination'),
          ) as PaginationData;

          return { changelogList: response.body, paginationData };
        }),
      );
  }

  // DELETE /experiment - TODO check URL
  deleteExperimentById(id: number): Observable<string> {
    return this.http
      .delete<string>(`${environment.apiUrl}/experiments/${id}`)
      .pipe(
        map((response: string) => {
          return response;
        }),
      );
  }

  // HTTP GET /ERRORLOG
  getExperimentDeviceErrorLog(
    id: number,
    sortBy = 'CreatedAt',
    sortOrder = 'asc',
    pageSize = 25,
    pageNumber = 1,
    errorTypeIds?: number[],
  ): Observable<DeviceErrorResponse> {
    let url = `${environment.apiUrl}/experiment-error-logs/${id}?OrderBy=${sortBy}&OrderByDirection=${sortOrder}&PageSize=${pageSize}&PageNumber=${pageNumber}`;
    if (errorTypeIds?.length > 0) {
      errorTypeIds.forEach((errorTypeId) => {
        url = url + `&ErrorTypes=${errorTypeId}`;
      });
    }
    return this.http.get<DeviceError[]>(url, this.httpOptions).pipe(
      map((response) => {
        // Set pagination data
        const paginationData = JSON.parse(
          response.headers.get('X-Pagination'),
        ) as PaginationData;

        return { deviceErrorsList: response.body, paginationData };
      }),
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
